import React, { useState, useEffect } from 'react';

// Utils
import { SortDirection } from 'ka-table/enums';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import PermissionsGate, { isGranted } from '../../../../../utils/gate/permissionsGate';
import { useAppContext } from '../../../../../store/AppContext';
import SCOPE from '../../../../../utils/gate/scope';
import languages from '../../../../../constants/languages';
import useNotification from '../../../../../utils/hooks/useNotification';

// Icons
import { ReactComponent as PlusIcon } from '../../../../../assets/icons/plus.svg';

// Services
import useAccommodationService from '../../../../../services/accommodationService';

// Components
import SkeletonLoader from '../../../../molecules/SkeletonLoader/SkeletonLoader';
import GenericTable from '../../../../tables/GenericTable/GenericTable';
import EmptyList from '../../../../organisms/EmptyList/EmptyList';
import DropdownActions from '../../../../molecules/DropdownActions/DropdownActions';
import Button from '../../../../atoms/Button/Button';

function Accommodations() {
  const { t } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const location = useLocation();
  const navigate = useNavigate();
  const [{ user }] = useAppContext();

  const { displayNotification } = useNotification();

  const { getAllAccommodations } = useAccommodationService();

  const [accommodations, setAccommodations] = useState([]);

  useEffect(() => {
    if (location?.state) {
      const notif = location.state;
      displayNotification({ message: t(notif) });
      navigate(location.pathname, { replace: true });
    }
  }, []);

  const { isFetching } = useQuery(
    ['getAllAccommodations'],
    () => getAllAccommodations(),
    {
      onSuccess: (res) => setAccommodations(res?.data?.accommodations || []),
    },
  );

  const columns = {
    name: {
      key: 'name', title: t('accommodations.table.columns.name'), sortDirection: SortDirection.Ascend, bold: true,
    },
    title: { key: 'title', title: t('accommodations.table.columns.title') },
    actions: { key: 'actions', title: t('accommodations.table.columns.actions') },
  };

  const getAccommodationRow = (accommodation, index) => ({
    id: index,
    name: accommodation.name,
    title: accommodation.translations.find((tr) => tr.language === languages.en_US)?.title,
    actions: (
      <div className="d-flex mobile-d-col mobile-a-end f-wrap">
        <DropdownActions
          id={`accommodationsActionsEdit${accommodation.id}`}
          rightAlign
          label={t('accommodations.table.actions.edit.label')}
          options={[
            {
              label: t('accommodations.table.actions.edit.options.marketing'),
              href: `/products/accommodations/marketing?id=${accommodation.id}`,
            },
            {
              label: t('accommodations.table.actions.edit.options.logistic'),
              href: `/products/accommodations/logistic?id=${accommodation.id}`,
            },
          ]}
          className="m-r-30 mobile-m-r-0"
        />

        <DropdownActions
          id={`accommodationsActionsTranslate${accommodation.id}`}
          rightAlign
          label={t('accommodations.table.actions.translate.label')}
          options={[
            {
              label: t('accommodations.table.actions.translate.options.french'),
              href: `/products/accommodations/translate?id=${accommodation.id}&lang=${languages.fr_FR}`,
            },
            {
              label: t('accommodations.table.actions.translate.options.english'),
              href: `/products/accommodations/translate?id=${accommodation.id}&lang=${languages.en_US}`,
            },
          ]}
        />
      </div>
    ),
  });

  return (
    (isFetching) ? (
      <>
        <SkeletonLoader height={60} className="m-b-20" />
        <SkeletonLoader height={300} />
      </>
    ) : (
      <>
        {accommodations?.length ? (
          <GenericTable
            columns={[columns.name, columns.title, columns.actions]}
            rows={accommodations.map((accommodation, index) => getAccommodationRow(accommodation, index))}
            isSearchable
            searchPlaceholder={t('accommodations.table.search')}
          />
        ) : (
          <EmptyList
            imageTheme="accommodations"
            title={t('accommodations.table.empty.title')}
            text={`
              ${t('accommodations.table.empty.text')} 
              ${isGranted(user, [SCOPE.create_ownEst_camp]) ? t('accommodations.table.empty.add') : ''}
            `}
          />
        )}

        {!isFetching ? (
          <PermissionsGate scopes={[SCOPE.create_ownEst_accommodation]}>
            <div className="d-flex a-center j-end sticky-actions mobile-a-stretch">
              <a href="/products/accommodations/new">
                <Button full={isMobile}>
                  <PlusIcon className="m-r-10" />
                  {t('accommodations.add')}
                </Button>
              </a>
            </div>
          </PermissionsGate>
        ) : null}
      </>
    )
  );
}

Accommodations.propTypes = {};

export default Accommodations;
